import { useEffect, useRef, useState } from "react"
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap"
import { Link } from "react-router-dom"

import { useCustomReportsFetch } from "../../../hooks/fetch/useCustomReportsFetch"
import { CustomReport } from "../../../interfaces/customReport"
import { getCountries } from "../../../data/api"
import { Country } from "../../../interfaces/locations"
import { formatDateToDMY } from "../../../utils/Functions"

const LIMIT = 100
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB, igual que el límite de la API

/**
 * Misma normalización que hace la API, para poder mostrar la URL final
 * mientras se escribe el nombre.
 */
const normalizeSlug = (value: string) => {
    if (!value) return ''

    return value
        .trim()
        .toLowerCase()
        .replace(/\.html?$/, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 150)
}

export const ListReports = () => {

    const { getReports, createReport, updateReport, deleteReport, showAlertMessage } = useCustomReportsFetch()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [reports, setReports] = useState<CustomReport[]>([])
    const [countries, setCountries] = useState<Country[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [search, setSearch] = useState('')

    // Formulario (sirve para crear y para editar)
    const [editing, setEditing] = useState<CustomReport | null>(null)
    const [countryId, setCountryId] = useState<number | ''>('')
    const [slug, setSlug] = useState('')
    const [title, setTitle] = useState('')
    const [sandboxed, setSandboxed] = useState(true)
    const [file, setFile] = useState<File | null>(null)

    useEffect(() => {
        loadReports()
        getCountries().then(res => {
            if (res && res.success && res.data) setCountries(res.data)
        }).catch(err => console.log(err))
    }, [])

    const loadReports = (searchTerm?: string) => {
        setLoading(true)

        getReports(1, LIMIT, undefined, searchTerm).then(res => {
            setLoading(false)
            if (res.success) {
                setReports(res.data)
            } else if (res.message) {
                showAlertMessage('danger', res.message)
            }
        }).catch(err => {
            setLoading(false)
            console.log(err)
        })
    }

    const resetForm = () => {
        setEditing(null)
        setCountryId('')
        setSlug('')
        setTitle('')
        setSandboxed(true)
        setFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const startEdit = (report: CustomReport) => {
        setEditing(report)
        setCountryId(report.country_id)
        setSlug(report.slug)
        setTitle(report.title || '')
        setSandboxed(report.sandboxed)
        setFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const handleFileChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const selected = ev.target.files && ev.target.files[0] ? ev.target.files[0] : null

        if (selected && selected.size > MAX_FILE_SIZE) {
            showAlertMessage('danger', 'El archivo supera el máximo de 10MB')
            ev.target.value = ''
            setFile(null)
            return
        }

        setFile(selected)

        // Si todavía no pusieron nombre, proponemos el del archivo
        if (selected && slug.trim() === '') setSlug(normalizeSlug(selected.name))
    }

    const handleSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault()

        const finalSlug = normalizeSlug(slug)

        if (finalSlug === '') {
            showAlertMessage('danger', 'Escribí el nombre de la URL (ej: titulo-primer-reporte)')
            return
        }

        if (!editing && !countryId) {
            showAlertMessage('danger', 'Elegí el país del reporte')
            return
        }

        if (!editing && !file) {
            showAlertMessage('danger', 'Adjuntá el archivo html')
            return
        }

        setSaving(true)

        const result = editing ?
            await updateReport({
                id: editing.id,
                slug: finalSlug,
                title: title.trim() !== '' ? title.trim() : finalSlug,
                sandboxed,
                file
            }) :
            await createReport({
                country_id: countryId as number,
                slug: finalSlug,
                title: title.trim(),
                sandboxed,
                file: file as File
            })

        setSaving(false)

        if (result.success) {
            showAlertMessage('success', result.message || 'Listo')
            resetForm()
            loadReports(search !== '' ? search : undefined)
        } else {
            showAlertMessage('danger', result.message || 'No se pudo guardar el reporte')
        }
    }

    const toggleActive = async (report: CustomReport) => {
        const result = await updateReport({ id: report.id, active: !report.active })

        if (result.success) {
            showAlertMessage('success', report.active ? 'Reporte desactivado' : 'Reporte activado')
            loadReports(search !== '' ? search : undefined)
        } else {
            showAlertMessage('danger', result.message || 'No se pudo actualizar el reporte')
        }
    }

    const handleDelete = async (report: CustomReport) => {
        const confirmed = window.confirm(`¿Eliminar el reporte "${report.title || report.slug}"? La URL deja de funcionar.`)
        if (!confirmed) return

        const result = await deleteReport(report.id)

        if (result.success) {
            showAlertMessage('success', 'Reporte eliminado')
            if (editing && editing.id === report.id) resetForm()
            loadReports(search !== '' ? search : undefined)
        } else {
            showAlertMessage('danger', result.message || 'No se pudo eliminar el reporte')
        }
    }

    const copyUrl = (report: CustomReport) => {
        if (!report.url) return

        navigator.clipboard.writeText(report.url).then(() => {
            showAlertMessage('success', 'URL copiada')
        }).catch(err => console.log(err))
    }

    const searchTimer = useRef<any>(0)
    const searchInput = (ev: any) => {
        const value = ev.target.value
        setSearch(value)

        clearTimeout(searchTimer.current)
        searchTimer.current = setTimeout(() => {
            loadReports(value.trim() !== '' ? value.trim() : undefined)
        }, 250)
    }

    const previewSlug = normalizeSlug(slug)

    return (
        <div className="p-4 p-md-5">

            <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap">
                <Link to="/admin" className="fs-2 go-back">
                    <i className="text-light bi bi-arrow-left"></i>
                </Link>
                <h3 className="mb-0">REPORTES</h3>
                <div style={{ width: 40 }}></div>
            </div>

            <Card className="mx-auto mb-5" style={{ maxWidth: 800 }}>
                <Card.Body className="p-4">

                    <h5 className="mb-3">
                        {editing ? `Editando: ${editing.slug}.html` : 'Publicar un reporte'}
                    </h5>

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={5}>
                                <Form.Group className="mb-3">
                                    <Form.Label>País *</Form.Label>
                                    <Form.Select
                                        value={countryId}
                                        disabled={editing !== null}
                                        onChange={(ev) => setCountryId(ev.target.value !== '' ? parseInt(ev.target.value) : '')}
                                    >
                                        <option value="">Seleccioná un país</option>
                                        {countries.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </Form.Select>
                                    {editing && (
                                        <Form.Text className="text-muted">
                                            El país no se puede cambiar. Si te equivocaste, eliminá el reporte y subilo de nuevo.
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col md={7}>
                                <Form.Group className="mb-3">
                                    <Form.Label>URL *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={slug}
                                        placeholder="titulo-primer-reporte"
                                        onChange={(ev) => setSlug(ev.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        Va a quedar en <strong>/reportes/{previewSlug !== '' ? previewSlug : '...'}.html</strong>
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Título</Form.Label>
                            <Form.Control
                                type="text"
                                value={title}
                                placeholder="Sólo para identificarlo en esta lista"
                                onChange={(ev) => setTitle(ev.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Archivo html {!editing && '*'}</Form.Label>
                            <Form.Control
                                type="file"
                                ref={fileInputRef as any}
                                accept=".html,.htm,text/html"
                                onChange={handleFileChange}
                            />
                            <Form.Text className="text-muted">
                                {editing ?
                                    'Dejalo vacío para conservar el html actual.' :
                                    'Un único archivo html, con las imágenes embebidas o apuntando a una URL completa.'}
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Check
                                type="checkbox"
                                id="sandboxed-check"
                                checked={sandboxed}
                                label="Aislar el reporte del portal (recomendado)"
                                onChange={(ev) => setSandboxed(ev.target.checked)}
                            />
                            <Form.Text className="text-muted">
                                El html se sirve en un origen aislado: puede ejecutar su propio javascript pero no accede
                                a la sesión de Iris. Destildalo sólo si el reporte necesita cookies o analytics propios.
                            </Form.Text>
                        </Form.Group>

                        <div className="d-flex gap-2">
                            <Button type="submit" variant="primary" disabled={saving}>
                                {saving && <Spinner animation="border" size="sm" className="me-2" />}
                                {editing ? 'Guardar cambios' : 'Publicar'}
                            </Button>

                            {editing && (
                                <Button type="button" variant="outline-secondary" onClick={resetForm} disabled={saving}>
                                    Cancelar
                                </Button>
                            )}
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Row className="mb-3">
                <Col md={5}>
                    <Form.Control
                        type="text"
                        placeholder="Buscar por título o URL"
                        value={search}
                        onChange={searchInput}
                    />
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                </div>
            ) : reports.length === 0 ? (
                <p className="text-muted text-center py-5">Todavía no hay reportes publicados.</p>
            ) : (
                <Table responsive hover className="align-middle">
                    <thead>
                        <tr>
                            <th>Título</th>
                            <th>URL</th>
                            <th>País</th>
                            <th>Estado</th>
                            <th>Actualizado</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {reports.map(report => (
                            <tr key={report.id}>
                                <td>{report.title || report.slug}</td>
                                <td>
                                    {report.url ? (
                                        <a href={report.url} target="_blank" rel="noreferrer">{report.url}</a>
                                    ) : (
                                        <span className="text-muted">/reportes/{report.slug}.html</span>
                                    )}
                                    {report.url && (
                                        <Button
                                            variant="link"
                                            size="sm"
                                            title="Copiar URL"
                                            onClick={() => copyUrl(report)}
                                        >
                                            <i className="bi bi-clipboard"></i>
                                        </Button>
                                    )}
                                </td>
                                <td>{report.country ? report.country.name : report.country_id}</td>
                                <td>
                                    <Badge bg={report.active ? 'success' : 'secondary'}>
                                        {report.active ? 'Activo' : 'Inactivo'}
                                    </Badge>
                                    {!report.sandboxed && (
                                        <Badge bg="warning" text="dark" className="ms-1" title="Se sirve sin aislar del portal">
                                            Sin aislar
                                        </Badge>
                                    )}
                                </td>
                                <td>{formatDateToDMY(report.updated_at, '/')}</td>
                                <td className="text-nowrap">
                                    <Button variant="outline-primary" size="sm" className="me-2" onClick={() => startEdit(report)}>
                                        Editar
                                    </Button>
                                    <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => toggleActive(report)}>
                                        {report.active ? 'Desactivar' : 'Activar'}
                                    </Button>
                                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(report)}>
                                        Eliminar
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    )
}
